import {
  getLangCode,
  getLangData,
  useAppDispatch,
  useAppSelector,
} from "@hypexone/uicore";
import { useState } from "react";

interface LanguageSelectorProps {
  className?: string;
  showActionButtons?: boolean;
  onApply?: () => void;
  onCancel?: () => void;
  applyOnSelect?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = "",
  showActionButtons = false,
  onApply,
  onCancel,
  applyOnSelect = false,
}) => {
  const dispatch = useAppDispatch();
  const { domainConfig } = useAppSelector((state) => state.common);
  const { langData } = useAppSelector((state) => state.cms);
  const languages = domainConfig.languages || [];

  const currentLang = sessionStorage.getItem("lang") || "en";
  const [pendingLang, setPendingLang] = useState(currentLang);

  const applyLanguage = (langCode: string) => {
    sessionStorage.setItem("lang", langCode);
    dispatch(getLangData(langCode));
    onApply?.();
  };

  const handleSelect = (langCode: string) => {
    setPendingLang(langCode);
    if (applyOnSelect) {
      applyLanguage(langCode);
    }
  };

  const handleUpdate = () => {
    applyLanguage(pendingLang);
  };

  if (!languages.length) {
    return (
      <div className={`text-gray-500 text-sm ${className}`}>
        {langData?.["no_languages_found"] ?? "No languages available"}
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="max-h-[320px] overflow-y-auto rounded border border-[#e0e0e0]">
        {languages.map((language) => {
          const langCode = getLangCode(language)?.toLowerCase() || "";
          const isSelected = pendingLang === langCode;

          return (
            <button
              key={language}
              type="button"
              onClick={() => handleSelect(langCode)}
              className={`flex w-full items-center gap-3 border-b border-[#e8e8e8] px-4 py-3 text-left text-[14px] transition-colors last:border-b-0 ${
                isSelected
                  ? "bg-[#e8f4fc] font-semibold text-[#2789ce]"
                  : "bg-white text-[#333] hover:bg-gray-50"
              }`}
            >
              <span
                className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 ${
                  isSelected
                    ? "border-[#2789ce] bg-[#2789ce]"
                    : "border-[#bbb] bg-white"
                }`}
                aria-hidden
              >
                {isSelected && (
                  <span className="h-[7px] w-[7px] rounded-full bg-white" />
                )}
              </span>
              <span>{language}</span>
            </button>
          );
        })}
      </div>

      {showActionButtons && (
        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded border border-[#ccc] bg-white px-4 py-2.5 text-[14px] font-semibold text-[#333] transition-colors hover:bg-gray-50"
          >
            {langData?.["close"] ?? "Close"}
          </button>
          <button
            type="button"
            onClick={handleUpdate}
            className="flex-1 rounded bg-[#1e2a3a] px-4 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#2a3a4f]"
          >
            {langData?.["update"] ?? "Update"}
          </button>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
